/*
 * r16p (2026-10-24), DESIGN D:/NecroWork/dm-chats-evidence/DESIGN.md 6.7 / 7.4 and api-security.md 4: the pure side of
 * /settings/api. The key list never carries a secret (the server keeps only its sha256); the one-time key of a create answer is
 * read by keyFromCreate() and kept by the page in React state only - never in the query cache, localStorage, a URL, a toast or a
 * console line.
 */

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const str = (v) => (typeof v === 'string' && v.trim() !== '' ? v : null);
const toMs = (v) => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v).trim();
  if (/^\d{10,}$/.test(s)) return Number(s);
  const t = Date.parse(s);
  return Number.isFinite(t) ? t : null;
};
const pick = (o, ...keys) => {
  if (!o || typeof o !== 'object') return undefined;
  for (let i = 0; i < keys.length; i++) if (Object.prototype.hasOwnProperty.call(o, keys[i]) && o[keys[i]] !== undefined) return o[keys[i]];
  return undefined;
};

/** The scopes V3 offers in r16p (webhooks:manage comes with webhooks in r16q, O22). */
export const SCOPES = [
  { value: 'chats:read', label: 'Read chats', help: 'Slots, conversations, messages, reply status and the events feed.', default: true },
  { value: 'replies:write', label: 'Queue replies', help: 'Queue and cancel replies.', default: false,
    warning: 'Messages queued with this key are sent to real people without review.' },
  { value: 'chats:delete', label: 'Delete conversations', help: 'Delete a conversation and everything stored for it.', default: false },
];
const SCOPE_VALUES = new Set(['chats:read', 'replies:write', 'chats:delete', 'webhooks:manage']);

export const EXPIRY_OPTIONS = [
  { value: '', label: 'Never' },
  { value: '90', label: 'In 90 days' },
  { value: '365', label: 'In 365 days' },
];

/** The key shape (api-security 4.1): nm_live_ + 10 base32 + _ + 43 base64url. */
export const KEY_RE = /^nm_live_[a-z2-7]{10}_[A-Za-z0-9_-]{43}$/;

export function normaliseKey(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = pick(raw, 'id', 'keyId', 'key_id');
  if (id === undefined || id === null) return null;
  // r16pm: the r16p server sends `display` = "nm_live_<key id>_...<last 4>" (ApiKeyService.row) and its `id` IS the key id; no
  // prefix / last4 fields. The display is parsed (only its known shape), so the row shows the key id and the last 4.
  const shown = str(raw.display) ? String(raw.display).trim().match(/^(nm_live_[a-z2-7]{10})_\.\.\.([A-Za-z0-9_-]{4})$/) : null;
  const prefix = str(raw.prefix) || (str(pick(raw, 'keyId', 'key_id')) ? `nm_live_${pick(raw, 'keyId', 'key_id')}` : null)
    || (shown ? shown[1] : null);
  const scopes = (Array.isArray(raw.scopes) ? raw.scopes : String(raw.scopes || '').split(/[\s,]+/))
    .map((s) => String(s).trim()).filter((s) => SCOPE_VALUES.has(s));
  const slots = Array.isArray(pick(raw, 'slots', 'slotNames', 'slot_names', 'slotIds', 'slot_ids')) ? pick(raw, 'slots', 'slotNames', 'slot_names', 'slotIds', 'slot_ids') : [];
  const revokedAt = toMs(pick(raw, 'revokedAt', 'revoked_at'));
  const expiresAt = toMs(pick(raw, 'expiresAt', 'expires_at'));
  return {
    id: String(id),
    name: str(raw.name) || 'API key',
    prefix,
    last4: str(raw.last4) || (shown ? shown[2] : null),
    scopes,
    allSlots: raw.allSlots === true || raw.all_slots === true || (slots.length === 0 && raw.allSlots !== false && raw.all_slots !== false),
    slots: slots.map((s) => (typeof s === 'number' ? `slot${s}` : String(s))),
    slotCount: num(pick(raw, 'slotCount', 'slot_count')),
    createdAt: toMs(pick(raw, 'createdAt', 'created_at')),
    lastUsedAt: toMs(pick(raw, 'lastUsedAt', 'last_used_at')),
    expiresAt,
    revokedAt,
    revokedReason: str(pick(raw, 'revokedReason', 'revoked_reason')),
  };
}

/** 'active' | 'revoked' | 'expired'. */
export function keyState(k, now = Date.now()) {
  if (!k) return 'revoked';
  if (k.revokedAt) return 'revoked';
  if (k.expiresAt && k.expiresAt <= now) return 'expired';
  return 'active';
}

/** "nm_live_k7f3q9x2m4_...a1b2" - the key id plus the last 4, the only form a key is ever shown in after creation. */
export function keyDisplay(k) {
  if (!k) return '';
  const tail = k.last4 ? `…${k.last4}` : '…';
  return k.prefix ? `${k.prefix}_${tail}` : `nm_live_${tail}`;
}

/** GET /uiapi/api-keys -> { keys, maxActive, limits, baseUrl, enabled, expiryDays }. */
export function normaliseKeyList(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const list = Array.isArray(r) ? r : (Array.isArray(r.keys) ? r.keys : []);
  const exp = Array.isArray(pick(r, 'expiryDays', 'expiry_days')) ? pick(r, 'expiryDays', 'expiry_days') : null;
  return {
    keys: list.map(normaliseKey).filter(Boolean),
    maxActive: num(pick(r, 'maxActive', 'max_active', 'maxKeys')),
    limits: normaliseLimits(r.limits || r),
    baseUrl: str(pick(r, 'baseUrl', 'base_url')),
    // r16pm: the r16p server says `chatsEnabled` (false = necro.server.chats is OFF: list, revoke and Lock stay; create answers 404)
    enabled: r.enabled !== false && r.chatsEnabled !== false,
    expiryDays: exp ? exp.map(num).filter((d) => d !== null && d > 0) : null,
  };
}

/** The API's numbers (rate limits, page sizes, the events window). Every one may be missing: its docs line is then left out. */
export function normaliseLimits(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  return {
    perKeyPerMinute: num(pick(r, 'perKeyPerMinute', 'per_key_per_minute', 'keyPerMinute')),
    perAccountPerMinute: num(pick(r, 'perAccountPerMinute', 'per_account_per_minute', 'accountPerMinute')),
    pageLimit: num(pick(r, 'pageLimit', 'page_limit', 'defaultLimit')),
    pageMax: num(pick(r, 'pageMax', 'page_max', 'maxLimit')),
    eventsDays: num(pick(r, 'eventsDays', 'events_days', 'eventsRetentionDays')),
    maxReplyLength: num(pick(r, 'maxReplyLength', 'max_reply_length')),
    retentionDays: num(pick(r, 'retentionDays', 'retention_days')),
    // review: the Docs tab's reply window and repeat window - drawn only when the server sends them (no hard-coded 30 days / a day)
    replyWindowDays: num(pick(r, 'replyWindowDays', 'reply_window_days')),
    repeatWindowHours: num(pick(r, 'repeatWindowHours', 'repeat_window_hours')),
  };
}

/** The one-time key in a create answer ({ key } or { secret }), only when it has the key shape; else null. */
export function keyFromCreate(data) {
  const d = data && typeof data === 'object' ? data : {};
  const k = typeof d.key === 'string' ? d.key : (typeof d.secret === 'string' ? d.secret : (d.apiKey && typeof d.apiKey.key === 'string' ? d.apiKey.key : null));
  return k && KEY_RE.test(k.trim()) ? k.trim() : null;
}

/**
 * The create body: name, scopes, all slots or slot NAMES, expiry in days (null = never). r16pm: the r16p server
 * (ApiKeysUiController.create) reads `expiresInDays` (90 or 365) and takes `slotNames` (exact names of this account; one unknown name
 * = 422 slot_not_found) or `slotIds`. The old `expiresDays` was silently ignored there: every key would have been created
 * never-expiring.
 */
export function createBody({ name, scopes, allSlots, slotNames, expiresDays }) {
  const body = {
    name: String(name || '').trim().slice(0, 100),
    scopes: (scopes || []).filter((s) => SCOPE_VALUES.has(s)),
    allSlots: !!allSlots,
  };
  if (!allSlots) body.slotNames = (slotNames || []).slice(0, 5000);
  const d = num(expiresDays);
  body.expiresInDays = d && d > 0 ? d : null;
  return body;
}
